/**
 * Cluster preference persistence using browser localStorage
 */

import type { ClusterPreferences } from './types.js';
import { DEFAULT_PREFERENCES } from './types.js';

const STORAGE_KEY = 'le-spoons:cluster-preferences';

/**
 * Load cluster preferences from localStorage
 * Falls back to DEFAULT_PREFERENCES if unavailable or invalid
 * @returns ClusterPreferences object (never null)
 */
export function loadPreferences(): ClusterPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      console.log('No stored preferences found, using defaults');
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(stored);

    // Validate schema version
    if (parsed.version !== 1) {
      console.warn(`Unknown preference version ${parsed.version}, using defaults`);
      return DEFAULT_PREFERENCES;
    }

    // Validate clusteringEnabled
    if (typeof parsed.clusteringEnabled !== 'boolean') {
      console.warn('Invalid clusteringEnabled type, using defaults');
      return DEFAULT_PREFERENCES;
    }

    // Normalize the stored value so legacy clusterSize fields are harmlessly dropped.
    const preferences: ClusterPreferences = {
      version: 1,
      clusteringEnabled: parsed.clusteringEnabled,
    };
    console.log('Loaded preferences from localStorage:', preferences);
    return preferences;
  } catch (error) {
    console.warn('Failed to load preferences from localStorage:', error);
    console.warn('Using default preferences');
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Save cluster preferences to localStorage
 * Gracefully handles localStorage unavailability
 * @param prefs - ClusterPreferences to save
 */
export function savePreferences(prefs: ClusterPreferences): void {
  try {
    const serialized = JSON.stringify(prefs);
    localStorage.setItem(STORAGE_KEY, serialized);
    console.log('Saved preferences to localStorage:', prefs);
  } catch (error) {
    console.warn('Failed to save preferences to localStorage:', error);
    console.warn('Preferences will not persist across sessions');
    // Continue - controls still work, just won't persist
  }
}
